import React, { useState } from 'react';
import { 
  ExclamationTriangleIcon,
  XCircleIcon,
  LockClosedIcon,
  KeyIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import Dialog from '../common/Dialog';

export interface UserActionConfirmModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
  user: any;
  action: 'block' | 'unblock' | 'force-password-change' | 'delete';
  loading?: boolean;
}

const UserActionConfirmModal: React.FC<UserActionConfirmModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  user,
  action,
  loading = false
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleConfirm = async () => {
    try {
      setIsSubmitting(true);
      await onConfirm();
      onClose();
    } catch (error) {
      console.error('Erreur lors de l\'action:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getActionConfig = () => {
    switch (action) {
      case 'block':
        return {
          title: 'Bloquer l\'utilisateur',
          icon: <LockClosedIcon className="h-8 w-8 text-red-500" />,
          message: `Êtes-vous sûr de vouloir bloquer l'utilisateur ${user?.name || user?.username} ?`,
          description: 'L\'utilisateur ne pourra plus se connecter à l\'application.',
          confirmText: 'Bloquer l\'utilisateur',
          confirmColor: 'bg-red-600 hover:bg-red-700',
          iconColor: 'text-red-500'
        };
      case 'unblock':
        return {
          title: 'Débloquer l\'utilisateur',
          icon: <KeyIcon className="h-8 w-8 text-green-500" />,
          message: `Êtes-vous sûr de vouloir débloquer l'utilisateur ${user?.name || user?.username} ?`,
          description: 'L\'utilisateur pourra à nouveau se connecter à l\'application.',
          confirmText: 'Débloquer l\'utilisateur',
          confirmColor: 'bg-green-600 hover:bg-green-700',
          iconColor: 'text-green-500'
        };
      case 'force-password-change':
        return {
          title: 'Forcer le changement de mot de passe',
          icon: <ClockIcon className="h-8 w-8 text-orange-500" />,
          message: `Forcer le changement de mot de passe pour ${user?.name || user?.username} ?`,
          description: 'L\'utilisateur devra changer son mot de passe lors de sa prochaine connexion.',
          confirmText: 'Forcer le changement',
          confirmColor: 'bg-orange-600 hover:bg-orange-700',
          iconColor: 'text-orange-500'
        };
      case 'delete':
        return {
          title: 'Supprimer l\'utilisateur',
          icon: <XCircleIcon className="h-8 w-8 text-red-500" />,
          message: `Êtes-vous sûr de vouloir supprimer l'utilisateur ${user?.name || user?.username} ?`,
          description: 'Cette action est irréversible et supprimera définitivement l\'utilisateur.',
          confirmText: 'Supprimer l\'utilisateur',
          confirmColor: 'bg-red-600 hover:bg-red-700',
          iconColor: 'text-red-500'
        };
      default:
        return {
          title: 'Confirmer l\'action',
          icon: <ExclamationTriangleIcon className="h-8 w-8 text-yellow-500" />,
          message: 'Êtes-vous sûr de vouloir effectuer cette action ?',
          description: 'Veuillez confirmer votre choix.',
          confirmText: 'Confirmer',
          confirmColor: 'bg-blue-600 hover:bg-blue-700',
          iconColor: 'text-blue-500'
        };
    }
  };

  const config = getActionConfig();

  if (!user) return null;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={config.title}
      size="md"
    >
      <div className="text-center space-y-6">
        {/* Icône d'action */}
        <div className="flex justify-center">
          <div className={`p-4 rounded-full bg-gray-100 ${config.iconColor}`}>
            {config.icon}
          </div>
        </div>

        {/* Message principal */}
        <div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {config.message}
          </h3>
          <p className="text-sm text-gray-500">
            {config.description}
          </p>
        </div>

        {/* Informations utilisateur */}
        <div className="bg-gray-50 rounded-lg p-4">
          <div className="flex items-center justify-center space-x-4">
            <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center">
              <span className="text-orange-600 font-medium text-sm">
                {user.name?.split(' ').map((n: string) => n[0]).join('') || user.username?.charAt(0).toUpperCase()}
              </span>
            </div>
            <div className="text-left">
              <p className="text-sm font-medium text-gray-900">{user.name || 'Nom non défini'}</p>
              <p className="text-xs text-gray-500">@{user.username}</p>
              {user.email && (
                <p className="text-xs text-gray-500">{user.email}</p>
              )}
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 pt-4">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
            disabled={loading || isSubmitting}
          >
            Annuler
          </button>
          <button
            onClick={handleConfirm}
            className={`flex-1 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${config.confirmColor}`}
            disabled={loading || isSubmitting}
          >
            {isSubmitting ? (
              <div className="flex items-center justify-center">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                Traitement...
              </div>
            ) : (
              config.confirmText
            )}
          </button>
        </div>

        {/* Avertissement pour les actions critiques */}
        {(action === 'delete' || action === 'block') && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <div className="flex items-center">
              <ExclamationTriangleIcon className="h-5 w-5 text-red-400 mr-2" />
              <p className="text-sm text-red-700">
                <strong>Attention :</strong> Cette action est critique et peut avoir des conséquences importantes.
              </p>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
};

export default UserActionConfirmModal;
